import React from 'react';
import { useEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import invariant from 'tiny-invariant';

import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { attachClosestEdge, extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';

import { getCardData, getCardDropTargetData, isCardData, isDraggingACard } from './Data.jsx';
import { isShallowEqual } from '@MyLib/System/isShallowEqual.js';

import { useTranslation } from "react-i18next";
import { useSave } from '../../context/SaveContext.jsx';
import { getCardDisplaySize } from '../../context/SaveContext.jsx';
import { MyButton } from '../ui/UI.jsx';
import { useCardSrc, ManaText } from '../mtg/CardSymbol.jsx';

const CardState = {
	Idle: 'idle',
	IsDragging: 'is-dragging',
	IsDraggingAndLeftSelf: 'is-dragging-and-left-self',
	IsOver: 'is-over',
	Preview: 'preview',
};
const idle = { type: CardState.Idle };

const innerStyles = {
	[CardState.Idle]: 'cursor-grab hover-outline',//機能していない
	[CardState.IsDragging]: 'opacity-40'
};

const outerStyles = {
	[CardState.IsDraggingAndLeftSelf]: 'd-none'
};

export function CardShadow({ dragging }) {
	return <div className="flex-shrink-0 rounded bg-dark shadow-sm" style={{ height: dragging.height }} />;
}

const CardDiscription = ({card, cardData, state, cardSelector}) => {
	const {t} = useTranslation();
	const {setBuildCard} = useSave();
	const cardSrc = useCardSrc();

	useEffect(() => {
		cardSrc.reset();
	}, [cardData]);

	const onClickAdd = (v) => {
		const newCard = {
				...card,
				num: card.num + v
			}
		setBuildCard(newCard);
	};

	return (
		<div
			className="p-0 start-0 w-100"
			style={{ cursor: 'pointer' }}
		>
			<div className="d-flex align-items-center">
				<div className="text-truncate">
					{cardData.name}
				</div>
				<div className="ms-auto">
					<ManaText text={cardData.manaCost} wrap={false} />
				</div>
			</div>
			<div className="position-relative overflow-hidden"
				style={{height: getCardDisplaySize()}}
			>
				<img
					src={cardSrc.getSrc(card.language, cardData.setCode, "front", card.uuid)}
					onError={cardSrc.onError}
					className="overflow-hidden"
					style={{
					width: "auto", height: "480px", transform: "translate(-24px, -54px)",
						pointerEvents: "none"
					}}
				/>
				{(state.type === CardState.Idle) &&
					<div className="position-absolute bottom-0 end-0 m-0 d-flex gap-1 align-items-center ">
						{(cardSelector.selectCard != null && cardSelector.selectCard.id === card.id) &&
							<MyButton
								onClick={(e)=>{
									cardSelector.setShowCard({id: card.id, uuid: card.uuid, setCode: cardData.setCode});
									e.stopPropagation();
								}}
								text={t("Common_Open")}
							/>
						}
						{(cardSelector.selectCard != null && cardSelector.selectCard.id === card.id) &&
							<MyButton
								onClick={(e)=>{
									onClickAdd(-1);
									e.stopPropagation();
								}}
								text=" - "
							/>
						}
						<span className='badge bg-dark text-white'>{card.num}</span>
						{(cardSelector.selectCard != null && cardSelector.selectCard.id === card.id) &&
							<MyButton
								onClick={(e)=>{
									onClickAdd(+1);
									e.stopPropagation();
								}}
								text=" + "
							/>
						}
					</div>
				}
			</div>
		</div>
	);
};

export const CardDisplay = ({
	card,
	cardData,
	state,
	outerRef,
	innerRef,
	cardSelector
}) =>
{
	const [hovered, setHovered] = useState(false);

	useEffect(() => {
		if (state !== CardState.Idle)
		{
			setHovered(false);
		}
	}, [state]);

	{/* マウスカーソルの手 */}
	return (
		<div
			ref={outerRef}
			className={`d-flex flex-column flex-shrink-0 ${outerStyles[state.type]}`}
			onMouseEnter={()=>{
				setHovered(true);
			}}
			onMouseLeave={()=>{
				setHovered(false);
			}}
			onClick={(e) => {
				if (cardSelector.selectCard && cardSelector.selectCard.id === card.id) {
					cardSelector.setSelectCard(null);
				}
				else {
					cardSelector.setSelectCard({id: card.id, uuid: card.uuid, setCode: cardData.setCode});
				}
			}}
		>
			{/* Put a shadow before the item if closer to the top edge */}
			{ state.type === CardState.IsOver && state.closestEdge === 'top' ? (
				<CardShadow dragging={state.dragging} />
			) : null }
			<div
				className={`bg-light ${innerStyles[state.type]}`}
				ref={innerRef}
				style={
					state.type === CardState.Preview
					? {
						width: state.dragging.width,
						height: state.dragging.height
					}
					: undefined
				}
			>
				<CardDiscription card={card} cardData={cardData} state={state} cardSelector={cardSelector} />
			</div>
			{/* Put a shadow after the item if closer to the bottom edge */}
			{ state.type === CardState.IsOver && state.closestEdge === 'bottom' ? (
				<CardShadow dragging={state.dragging} />
			) : null }
		</div>
	);
};

export const Card = ({ card, cardData, columnId, cardSelector }) =>
{	
	const outerRef = useRef(null);
	const innerRef = useRef(null);
	const [state, setState] = useState(idle);

	useEffect(() => {
		const outer = outerRef.current;
		const inner = innerRef.current;
		invariant(outer && inner);

		return combine(
			draggable({// ドラッグできる要素を設定
				element: inner,// ここを掴んでドラッグする
				getInitialData: ({element}) => getCardData({// この要素に関する情報、ドラッグ時に他へ渡すために使う
					card: card,
					columnId: columnId,
					rect: element.getBoundingClientRect()
				}),
				onGenerateDragPreview: ({nativeSetDragImage, location, source}) => {// プレビュー
					const data = source.data;
					invariant(isCardData(data));
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: preserveOffsetOnSource({ element: inner, input: location.current.input }),
						render({ container }) {
							// Demonstrating using a react portal to generate a preview
							setState({
								type: CardState.Preview,
								container,
								dragging: inner.getBoundingClientRect()
							});
						},
					});
				},
				onDragStart() {
					setState({ type: CardState.IsDragging });
				},
				onDrop() {
					setState(idle);
				}
			}),
			dropTargetForElements({
				element: outer,
				getIsSticky: () => true,
				canDrop: isDraggingACard,
				getData: ({ element, input }) => {
					const data = getCardDropTargetData({ card, columnId });
					return attachClosestEdge(data, { element, input, allowedEdges: ['top', 'bottom'] });
				},
				onDragEnter({ source, self }) {
					if (!isCardData(source.data)) {
						return;
					}
					if (source.data.card.id == card.id) {
						return;
					}
					const closestEdge = extractClosestEdge(self.data);
					if (!closestEdge) {
						return;
					}

					setState({ type: CardState.IsOver, dragging: source.data.rect, closestEdge: closestEdge });
				},
				onDrag({ source, self }) {
					if (!isCardData(source.data)) {
						return;
					}
					if (source.data.card.id == card.id) {
						return;
					}
					const closestEdge = extractClosestEdge(self.data);
					if (!closestEdge) {
						return;
					}
					// optimization - Don't update react state if we don't need to.
					const proposed = { type: CardState.IsOver, dragging: source.data.rect, closestEdge: closestEdge };
					setState((current) => {
						if (isShallowEqual(proposed, current)) {
							return current;
						}
						return proposed;
					});
				},
				onDragLeave({ source }) {
					if (!isCardData(source.data)) {
						return;
					}
					if (source.data.card.id === card.id) {
						setState({ type: CardState.IsDraggingAndLeftSelf });
						return;
					}
					setState(idle);
				},
				onDrop() {
					setState(idle);
				}
			})
		);
	}, [card, columnId]);

	return (
		<div>
			<CardDisplay outerRef={outerRef} innerRef={innerRef} state={state} card={card} cardData={cardData} cardSelector={cardSelector} />
			{ state.type === CardState.Preview
				? createPortal(<CardDisplay state={state} card={card} cardData={cardData} />, state.container)
				: null
			}
		</div>
	);
}
